import PropTypes from "prop-types";

import PageView from "~/components/PageView";
import QuickDial from "~/components/QuickDial";

import { styled } from "@mui/material/styles";
import { isAppleiPadDevice } from "~/utils";
import Cutout from "./Cutout";

const ViewerArea = styled("div")(() => ({
  backgroundColor: "#404040",
  backgroundImage: "url(/static/images/texture.png)",
  height: "100%",
  position: "relative",
  flex: "1 0",
  overflow: "hidden"
}));

const ViewerContainer = styled("div")(() => ({
  position: "relative",
  width: "100%",
  height: "100%"
}));

const ViewerWrapper = styled("div")(() => ({
  position: "absolute",
  left: "0",
  right: "0",
  top: "0",
  bottom: "0",
  overflow: "hidden"
}));

const ZoomParentContainer = styled("div")(() => ({
  position: "relative",
  width: "100%",
  height: "100%"
}));

const PagesContainer = styled("div")(props => ({
  transition: isAppleiPadDevice() ? "none" : "transform 0.2s ease-out 0s",
  willChange: "transform",
  width: `${props.totalPageWidth}px`,
  height: `${props.totalPageHeight}px`,
  backfaceVisibility: "hidden",
  "@media print": {
    transform: "none !important",
    transformOrigin: "initial !important",
    width: "100% !important",
    height: "100% !important"
  }
}));

function Viewer({
  zoomController,
  isDoublePage,
  pages,
  getThumbnailPath,
  getImagePath,
  showAnnotations,
  showMaterials,
  renderPagination,
  renderOutlineList,
  // This id determines the origin of annotations
  // book annotations leave this field empty
  // embedded annotations (viewer modal and dls preview) use this field
  // to determine their assignment
  bookId,
  quickDialActions,
  name,
  pageWidth,
  pageHeight,
  totalPageWidth,
  totalPageHeight,
  showCutout,
  isModalCutout = false // which cutout is it? from bookviewer or materialviewer?
}) {
  return (
    <ViewerArea className={`viewer viewer--${name}`}>
      {renderOutlineList()}
      {renderPagination()}

      <ViewerContainer>
        <ViewerWrapper>
          <ZoomParentContainer ref={zoomController.zoomParentRef}>
            <PagesContainer
              totalPageWidth={totalPageWidth}
              totalPageHeight={totalPageHeight}
              ref={zoomController.zoomChildRef}
            >
              <PageView
                viewerOrigin={name}
                bookId={bookId}
                isDoublePage={isDoublePage}
                pages={pages}
                getThumbnailPath={getThumbnailPath}
                getImagePath={getImagePath}
                zoomController={zoomController}
                showAnnotations={showAnnotations}
                showMaterials={showMaterials}
                pageWidth={pageWidth}
                pageHeight={pageHeight}
              />
              <Cutout
                key={`${bookId}-${pages.join(",")}`}
                zoomController={zoomController}
                active={showCutout}
                isModalCutout={isModalCutout}
              />
            </PagesContainer>
          </ZoomParentContainer>

          <QuickDial actions={quickDialActions} />
        </ViewerWrapper>
      </ViewerContainer>
    </ViewerArea>
  );
}

Viewer.whyDidYouRender = false;

Viewer.propTypes = {
  zoomController: PropTypes.object.isRequired,
  isDoublePage: PropTypes.bool,
  pages: PropTypes.array.isRequired,
  getThumbnailPath: PropTypes.func.isRequired,
  getImagePath: PropTypes.func.isRequired,
  showAnnotations: PropTypes.bool,
  showMaterials: PropTypes.bool,
  renderPagination: PropTypes.func.isRequired,
  renderOutlineList: PropTypes.func,
  bookId: PropTypes.string.isRequired,
  quickDialActions: PropTypes.array,
  name: PropTypes.string,
  pageWidth: PropTypes.number,
  pageHeight: PropTypes.number,
  totalPageWidth: PropTypes.number.isRequired,
  totalPageHeight: PropTypes.number.isRequired,
  showCutout: PropTypes.bool,
  isModalCutout: PropTypes.bool.isRequired
};

Viewer.defaultProps = {
  quickDialActions: [],
  renderOutlineList: () => {}
};

export default Viewer;
